<script setup>
import { computed } from 'vue';
import { format, parseISO } from 'date-fns';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';

const props = defineProps({
  event: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['click']);

const DEFAULT_COLOR = '#1F93FF';

const { getPlainText } = useMessageFormatter();

const isFollowUp = computed(() => props.event.sourceType === 'follow_up');

// Show the scheduled time for timed events (e.g. follow-ups); all-day events
// such as contact date attributes don't carry a meaningful time.
const formattedTime = computed(() => {
  if (props.event.allDay || !props.event.startsAt) return '';
  try {
    const start = format(parseISO(props.event.startsAt), 'p');
    if (!props.event.endsAt || props.event.endsAt === props.event.startsAt) {
      return start;
    }
    return `${start} – ${format(parseISO(props.event.endsAt), 'p')}`;
  } catch {
    return '';
  }
});

const messagePreview = computed(() => {
  if (!isFollowUp.value || !props.event.description) return '';
  return getPlainText(props.event.description);
});
</script>

<template>
  <button
    type="button"
    class="flex w-full gap-1 px-1.5 py-0.5 text-xs font-medium rounded-md text-start hover:opacity-80"
    :class="{ 'flex-col': isFollowUp }"
    :style="{
      backgroundColor: `${event.color || DEFAULT_COLOR}22`,
      color: event.color || DEFAULT_COLOR,
    }"
    @click.stop="emit('click', event)"
  >
    <span
      class="flex w-full gap-1"
      :class="isFollowUp ? 'items-center truncate' : 'items-baseline'"
    >
      <span v-if="formattedTime" class="shrink-0 tabular-nums opacity-80">
        {{ formattedTime }}
      </span>
      <span :class="isFollowUp ? 'truncate' : 'min-w-0 break-words'">
        {{ isFollowUp ? event.contactName || event.title : event.title }}
      </span>
    </span>
    <span v-if="isFollowUp" class="w-full font-normal truncate opacity-80">
      {{ messagePreview }}
    </span>
  </button>
</template>
